/**
 * Блок «Глава района» — содержимое белой карточки: аватар / ФИО / должность /
 * срок / телефон. §3 FE-5. Карточку-обёртку с заголовком секции и кнопкой
 * сворачивания рендерит ContextSidebar. Локализация: ru / kz — текущий язык
 * из useTranslation.
 */
import { Clock, Phone } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { AkimInfo } from '../../../types/public';

interface AkimProps {
  akim: AkimInfo;
}

/** Инициалы для аватара-заглушки: «Асанов Нурлан» → «АН». */
const initials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

/**
 * Месяцы для даты вступления в должность. Свои словари вместо
 * Intl.DateTimeFormat: не у всех браузеров есть ICU-данные казахской
 * локали (`kk`) — без них Intl падает в root-формат («2023 M07 1»).
 */
const MONTHS_RU = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
const MONTHS_KZ = [
  'қаңтар', 'ақпан', 'наурыз', 'сәуір', 'мамыр', 'маусым',
  'шілде', 'тамыз', 'қыркүйек', 'қазан', 'қараша', 'желтоқсан',
];

/** «9 августа 2024 г.» (ru) / «9 тамыз 2024 ж.» (kz). */
const formatSinceDate = (iso: string, isKz: boolean): string | null => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.getDate();
  const year = date.getFullYear();
  const months = isKz ? MONTHS_KZ : MONTHS_RU;
  return `${day} ${months[date.getMonth()]} ${year} ${isKz ? 'ж.' : 'г.'}`;
};

export const Akim = ({ akim }: AkimProps) => {
  const { i18n } = useTranslation();
  const [photoFailed, setPhotoFailed] = useState(false);
  const isKz = i18n.language === 'kz';
  const name = isKz ? akim.name_kz : akim.name_ru;

  // Должность выводится заголовком карточки-обёртки (ContextSidebar);
  // без ФИО контент не рендерим — карточка остаётся с одним заголовком.
  if (!name) {
    return null;
  }

  const sinceDate = akim.in_position_from
    ? formatSinceDate(akim.in_position_from, isKz)
    : null;

  return (
    <div data-testid="akim-card" className="mt-3">
      <div className="flex items-center gap-3.5">
        {/* Фото как в v1: портрет 2:3 почти без обрезки — исходники
            вертикальные, лицо в середине кадра (обрезка по центру). */}
        {akim.image_url && !photoFailed ? (
          <img
            src={akim.image_url}
            alt={name ?? ''}
            className="h-28 w-24 shrink-0 rounded-xl object-cover"
            data-testid="akim-photo"
            // Ссылка есть, а блоб не отдался (удалён, 401 на закрытом стенде) —
            // браузер иначе рисует значок сломанной картинки и дублирует ФИО
            // alt-текстом. Инициалы для этого уже есть.
            onError={() => setPhotoFailed(true)}
          />
        ) : (
          <span className="flex h-28 w-24 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-2xl font-bold text-brand-600">
            {initials(name)}
          </span>
        )}
        {/* ФИО — по центру оставшейся ширины (замечание дизайн-ревью). */}
        <div className="min-w-0 flex-1 text-center">
          {/* Перенос — только между словами (break-normal, замечание
              ревью): фамилия по слогам не рвётся. */}
          <h2
            className="break-normal text-[15px] font-semibold leading-snug text-text"
            data-testid="akim-name"
          >
            {name}
          </h2>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2.5 text-[13px] text-text-muted">
        {sinceDate !== null && (
          <div className="flex items-center gap-3" data-testid="akim-since">
            <Clock className="size-4 shrink-0 text-text-faint" />
            <span>
              {/* Дата — ярким брендовым цветом, с новой строки
                  (замечание дизайн-ревью). */}
              <Trans
                i18nKey="sidebar.inPositionSince"
                values={{ date: sinceDate }}
                components={{
                  hl: <span className="block font-semibold text-brand-600" />,
                }}
              />
            </span>
          </div>
        )}
        {akim.phone && (
          <div className="flex items-center gap-3" data-testid="akim-phone">
            <Phone className="size-4 shrink-0 text-text-faint" />
            <span>{akim.phone}</span>
          </div>
        )}
      </div>
    </div>
  );
};
